/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-10-06 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Badge, Button, ButtonGroup, SkeletonDisplayText, TextField } from '@shopify/polaris';
import { DeleteMinor, ChevronUpMinor, ChevronDownMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CSSTransition } from 'react-transition-group';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { RECOMMENDATION_WIDGET_RULE_GROUP_PRODUCT_OPTIONS as PRODUCT_OPTIONS } from 'src/constants/recommendation';

import type { RuleGroupType } from 'src/models/recommendation';
import ConditionProductItem from './condition-product-item';
import ConsequenceProductItem from './consequence-product-item';

import { reorder } from 'src/helpers';

type Props = {
  rules: Array<RuleGroupType>,
  rule: RuleGroupType,
  index: number,
  updateRulesField: Function,
  toggleModal: Function
};

type ComposedProps = WithI18nProps & Props;

class RuleGroupItem extends Component<ComposedProps> {
  state = {
    activeCollapse: true,
    productOptions: []
  };

  componentDidMount() {
    const { i18n } = this.props;
    const formattedProductOptions = PRODUCT_OPTIONS.map(function (option) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });
    this.setState({ productOptions: formattedProductOptions });
  }

  render() {
    const { i18n, index, toggleModal, rules } = this.props;
    const { activeCollapse, productOptions } = this.state;

    return (
      <div className="widget-rule-group">
        <div className={`rule-group-title ${activeCollapse ? 'active' : ''}`}>
          <div className="title">
            <span className="number">{index + 1}</span>
            {i18n.translate('AdminHTML.Recommendation.WidgetItem.group_Label')}
          </div>
          <div className="action">
            <ButtonGroup spacing="extraTight">
              {rules.length > 1 && <Button plain icon={DeleteMinor} onClick={this.deleteRule} />}
              <Button
                plain
                icon={activeCollapse ? ChevronUpMinor : ChevronDownMinor}
                onClick={this.toggleContent}
              />
            </ButtonGroup>
          </div>
        </div>
        <div className="rule-group-content">
          <CSSTransition
            in={activeCollapse}
            timeout={200}
            classNames="item-transition transition"
            unmountOnExit>
            <div>
              <div className="rule-group-block">
                <div>
                  <p className="field-title">
                    {i18n.translate('AdminHTML.Recommendation.WidgetItem.group_Conditions_Label')}
                  </p>
                  <p className="field-helper">
                    {i18n.translate('AdminHTML.Recommendation.WidgetItem.group_Conditions_Helper')}
                  </p>
                </div>
                <div className="field-array-content">
                  <div className="connect-right">
                    <Badge status="warning">
                      {i18n.translate('AdminHTML.Recommendation.WidgetItem.group_Conditions_Badge')}
                    </Badge>
                  </div>
                  <div className="main">
                    <div className="header">
                      {/* Currently, we have only one option to select */}
                      <TextField autoComplete="off" value={productOptions[0]?.label} />
                      <TextField
                        autoComplete="off"
                        onFocus={() => toggleModal('activeSelectConditionProducts', index)}
                        placeholder={i18n.translate(
                          'AdminHTML.Recommendation.WidgetItem.group_Conditions_Search'
                        )}
                        value=""
                      />
                    </div>
                    <div className="body">{this.renderConditionsContent()}</div>
                  </div>
                </div>
              </div>
              <div className="rule-group-block">
                <div>
                  <p className="field-title">
                    {i18n.translate('AdminHTML.Recommendation.WidgetItem.group_Consequences_Label')}
                  </p>
                  <p className="field-helper">
                    {i18n.translate(
                      'AdminHTML.Recommendation.WidgetItem.group_Consequences_Helper'
                    )}
                  </p>
                </div>
                <div className="field-array-content">
                  <div className="connect-right">
                    <Badge status="info">
                      {i18n.translate(
                        'AdminHTML.Recommendation.WidgetItem.group_Consequences_Badge'
                      )}
                    </Badge>
                  </div>
                  <div className="main">
                    <div className="header">
                      {/* Currently, we have only one option to select */}
                      <TextField autoComplete="off" value={productOptions[0]?.label} />
                      <TextField
                        autoComplete="off"
                        onFocus={() => toggleModal('activeSelectConsequenceProducts', index)}
                        placeholder={i18n.translate(
                          'AdminHTML.Recommendation.WidgetItem.group_Consequences_Search'
                        )}
                        value=""
                      />
                    </div>
                    <div className="body">{this.renderConsequencesContent()}</div>
                  </div>
                </div>
              </div>
            </div>
          </CSSTransition>
        </div>
      </div>
    );
  }

  renderConditionsContent = () => {
    const { rule, isLoading } = this.props;
    const { conditions = {} } = rule;
    const { value = [] } = conditions[0];

    if (isLoading) {
      return (
        <div className="product__skeleton">
          <SkeletonDisplayText size="medium" />
        </div>
      );
    }

    if (_.isEmpty(value)) {
      return '';
    }

    return (
      <ul className="product-list condition-list">
        {value.map((product, index) => (
          <li key={product.id ? product.id : index} className="product-item">
            <ConditionProductItem product={product} onRemove={this.onRemove} productIndex={index} />
          </li>
        ))}
      </ul>
    );
  };

  renderConsequencesContent = () => {
    const { rule, isLoading, i18n } = this.props;
    const { consequences = {} } = rule;
    const { value = [] } = consequences[0];

    if (isLoading) {
      return (
        <div className="product__skeleton">
          <SkeletonDisplayText size="medium" />
        </div>
      );
    }

    if (_.isEmpty(value)) {
      return '';
    }

    return (
      <>
        <p className="field-helper" style={{ marginTop: '0.5rem' }}>
          {i18n.translate('AdminHTML.Recommendation.WidgetItem.group_Consequences_DragDrop_Helper')}
        </p>
        <DragDropContext onDragEnd={this.handleDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <ul
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="product-list consequence-list">
                {value.map((product, index) => (
                  <Draggable
                    key={product.id ? product.id : index}
                    draggableId={`product-${product.id ? product.id : index}`}
                    index={index}>
                    {(provided, snapshot) => {
                      return (
                        <li
                          className="product-item"
                          ref={provided.innerRef}
                          {...provided.draggableProps}>
                          <ConsequenceProductItem
                            product={product}
                            draggableProps={provided.draggableProps}
                            dragHandleProps={provided.dragHandleProps}
                            onRemove={this.onRemove}
                            productIndex={index}
                          />
                        </li>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </>
    );
  };

  toggleContent = () => {
    this.setState(({ activeCollapse }) => ({
      activeCollapse: !activeCollapse
    }));
  };

  handleDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // reordered index of products and update
    const { index, rule, updateRulesField, rules } = this.props;
    let { consequences = [] } = rule;
    const { value } = consequences[0];

    const reorderedProducts = reorder(value, result.source.index, result.destination.index);

    let clonedRules = _.cloneDeep(rules);
    clonedRules[index].consequences[0].value = reorderedProducts;

    updateRulesField(clonedRules);
  };

  deleteRule = () => {
    const { index, rules, updateRulesField } = this.props;

    let clonedRules = _.cloneDeep(rules);
    clonedRules.splice(index, 1);

    updateRulesField(clonedRules);
  };

  onRemove = (fieldName, itemIndex) => {
    let { index, rule, rules, updateRulesField } = this.props;
    let listValue = _.cloneDeep(_.get(rule, `${[fieldName]}[0].value`));

    listValue.splice(itemIndex, 1);

    let clonedRules = _.cloneDeep(rules);
    clonedRules[index][fieldName][0].value = listValue;

    updateRulesField(clonedRules);
  };
}

export default withI18n()(RuleGroupItem);
